import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import _get from "lodash/get";
import _toNumber from "lodash/toNumber";
import _isNumber from "lodash/isNumber";
import _isEmpty from "lodash/isEmpty";
import translateText, { translateTimestamp } from "./translate";
import { Input, Button } from "reactstrap";
import AsyncSelect, { useAsync } from "react-select/async";
import { FaAngleDown, FaAngleUp, FaPen, FaLaptopMedical } from "react-icons/fa";
import { openTouchInput } from "./touchInput";
import moment from "moment";
import { getCalcValue } from "../utils/calculations";
import { postDataAPI } from "../api";

export const getSubGroupsDisplay = (has_sub_groups, inputValue, list, render_type, useLineBreaks) => {
  const returnData = {
    displayText: "",
    sum: 0,
    hasScore: false,
  };
  if (has_sub_groups) {
    for (const x of inputValue || []) {
      const li = list.find((y) => y.value === x.value) || {};
      if (_isNumber((li.config || {}).score)) {
        returnData.hasScore = true;
        returnData.sum += li.config.score;
      }
    }
    const displayArray = (inputValue || []).map((x) => {
      const li = list.find((y) => y.value === x.value) || {};
      return `${(li.config || {}).sub_group ? `${translateText((li.config || {}).sub_group)}: ` : ""}${
        (li.config || {}).score || translateText(x.label)
      }`;
    });
    returnData.displayText = displayArray.join(", ");
    if (useLineBreaks) returnData.displayText = displayArray.join("\n\n");
    if (render_type === "group_sum") {
      const sums = {};
      for (const x of inputValue || []) {
        const li = list.find((y) => y.value === x.value) || {};
        const group = li.group;
        if (!(group in sums)) sums[group] = 0;
        if (_isNumber((li.config || {}).score)) sums[group] += li.config.score;
        else sums[group] += parseInt(li.label);
      }
      returnData.displayText = Object.keys(sums)
        .map((group) => `${translateText(group)}: ${sums[group]}`)
        .join(", ");
    }
  }
  return returnData;
};

function FormField(props) {
  const inputRef = useRef();
  const {
    name,
    type,
    list,
    table = false,
    value,
    units,
    readOnly,
    inModal,
    onClickEffect,
    isFull = false,
    isFilter = false,
    hideLabel,
    inputWidth = "50%",
    config,
    showPencil,
    useTouchInputNav = true,
    hideUnits,
    data,
    fromDevice,
    listOptionsWidth,
    allData,
  } = props;

  const inputValue = type === "multiselect" ? (value || {}).value : value;
  const { min, max, has_sub_groups, render_type } = config || {};
  const unit = !hideUnits ? (inputValue || {}).unit || (units || [])[0] || "" : "";
  const [medication, setMedication] = useState(false);
  const [localDisplayText, setLocalDisplayText] = useState(null); // Add local state for immediate updates
  const medicationRef = useRef("");
  
  // Add username state and loading state
  const [usernameFromAPI, setUsernameFromAPI] = useState(null);
  const [isLoadingUsername, setIsLoadingUsername] = useState(false);

  const systemInfo = useSelector((state) => {
    return state.app.system.system_info;
  });

  // Reset local display text when value prop changes from parent
  useEffect(() => {
    setLocalDisplayText(null);
  }, [value]);

 // Add useEffect to fetch username when needed AND auto-populate it
useEffect(() => {
  const shouldFetchUsername = 
    (name === 'Advisor_or_ Prescriber' || name === "requestor" || name === "completed_by") &&
    (!inputValue || !inputValue.value);
  
  if (shouldFetchUsername && !usernameFromAPI && !isLoadingUsername) {
    loadUsernameOption();
  }
  
  // Auto-populate the field when username is fetched
  if (usernameFromAPI && shouldFetchUsername && props.onChange) {
    const usernameValue = {
      label: usernameFromAPI,
      value: usernameFromAPI
    };
    props.onChange(usernameValue);
  }
}, [name, inputValue, usernameFromAPI, isLoadingUsername]);

  // Function to load username from API
  // Function to load username from API
  async function loadUsernameOption() {
    setIsLoadingUsername(true);
    try {
      const response = await postDataAPI("/flowsheet/get-username", {});
      let username = null;
      
      if (response && response.username) {
        username = response.username;
        setUsernameFromAPI(username);
      } else {
        // Fallback to localStorage if API fails
        username = localStorage.getItem("username");
        setUsernameFromAPI(username);
      }
      
      // Auto-populate the field with the fetched username
      if (username && props.onChange) {
        const usernameValue = {
          label: username,
          value: username
        };
        props.onChange(usernameValue);
      }
    } catch (error) {
      console.error("Error fetching username:", error);
      // Fallback to localStorage if API fails
      const username = localStorage.getItem("username");
      setUsernameFromAPI(username);
      
      // Auto-populate even on error
      if (username && props.onChange) {
        const usernameValue = {
          label: username,
          value: username
        };
        props.onChange(usernameValue);
      }
    } finally {
      setIsLoadingUsername(false);
    }
  }

  function onChange(e, u) {
    console.log(`e: ${JSON.stringify(e)}`);
    console.log(`u: ${JSON.stringify(u)}`);
    
    // Update local state immediately for responsive display - BEFORE creating the object
    if (type === "text" || type === "numeric" || type === "search") {
      setLocalDisplayText(e);
    }
    
    let updatedValue = e;
    
    if (type === "text" || type === "numeric" || type === "timestamp" || type === "date" || type === "multiline" || type === "search") {
      if (type === "search") {
        updatedValue = { label: e, value: e };
        updatedValue.unit = u || (units || [])[0] || undefined;
      } else {
        updatedValue = { label: e, value: e };
        updatedValue.unit = u || (units || [])[0] || undefined;
      }
    } else if (type === "multiselect") {
      updatedValue = { value: e };
    }
    
    if (props.onChange) return props.onChange(updatedValue);
  }

  let hasUnit = false;
  
  // Use localDisplayText if available (during typing), otherwise calculate from value prop
  // This must be done BEFORE any other displayText transformations
  let displayText;
  
  // For text/numeric/search types that use keyboard input, prefer localDisplayText
  if (localDisplayText !== null && (type === "text" || type === "numeric" || type === "search")) {
    displayText = localDisplayText;
  } else {
    displayText = _get(inputValue, "label", "");
  }
  
  if ((displayText || displayText === 0) && unit && (type === "text" || type === "numeric" || type === "search")) {
    hasUnit = true;
    displayText = `${displayText} ${unit}`;
  }
  
  // Calculate sub groups OUTSIDE the condition so variables are available
  const { hasScore, sum, displayText: subGroupDisplay } = getSubGroupsDisplay(has_sub_groups, inputValue, list, render_type);
  
  // Only apply these transformations if we're NOT using localDisplayText
  if (localDisplayText === null) {
    if (type === "multiselect") {
      displayText = (inputValue || [])
        .map((m) => (m.isCustom ? (m || {}).label : translateText((m || {}).label, true)))
        .filter((f) => f)
        .join(", ");
    } else if (type === "select") {
      const selInput = inputValue || {};
      displayText = (selInput.value || "").includes(",") ? selInput.label : translateText(selInput.label, true);
      if (list) {
        // use config.score instead of label if present
        const li = list.find((x) => x.value === (inputValue || {}).value) || {};
        if (li && li.config && li.config.score) displayText = li.config.score;
      }
    } else if (type === "timestamp") {
      displayText = (inputValue || {}).value
        ? translateTimestamp(moment(inputValue.value).format(`${systemInfo.date_format} ${systemInfo.time_format}`), systemInfo.date_format)
        : "";
    } else if (type === "date") {
      displayText = (inputValue || {}).value ? translateTimestamp(moment(inputValue.value).format(systemInfo.date_format), systemInfo.date_format) : "";
    } else if (type === "readonly" && (config || {}).calculation) {
      hasUnit = true;
      let d = data;
      if (_get(config, "calculation.parameters", []).includes("$date_of_birth")) {
        const patData = useSelector((state) => {
          const [patient] = (state.app.staticData["MySubscriber::MyPatientReader"] || []).filter((f) => f.pat_admit_state && f.pat_admit_state !== 8);
          const patientId = (patient || {}).patient_id;
          return patientId ? patient : {};
        });
        d = { ...patData, ...data };
      }
      const calcValue = getCalcValue(config.calculation, d);
      displayText = `${calcValue.value !== null ? calcValue.value : ""} ${calcValue.unit || ""}`;
    }

    // Use API username with fallback to localStorage
    if(name == 'Advisor_or_ Prescriber' || name == "requestor" || name == "completed_by"){
      displayText = (inputValue && inputValue.value) ? 
        inputValue.value : (usernameFromAPI || localStorage.getItem("username"));
    }
    
    if (has_sub_groups) {
      displayText = subGroupDisplay;
    }
  }

  // if rendering FormField within a table
  if (table) {
    let className = "";
    if (isInteractive()) className += "input-nav-selector";
    if (has_sub_groups && hasScore) className += " d-flex";
    return (
      <div className={className} id={props.uniqueId} onClick={onClick} style={{ height: "100%", width: "100%" }}>
        {has_sub_groups && hasScore && (
          <div className="input-table-cell mr-2" style={{ whiteSpace: "nowrap", width: "30%" }}>
            {translateText("tot")}: {sum}
          </div>
        )}
        <div className="input-table-cell">
          {renderInput()}
          {!readOnly && showPencil && <FaPen size="14" className="pen-icon-color ml-2" />}
        </div>
      </div>
    );
  }

  let width = "50%";
  if (inModal) width = "100%";
  if (isFull) width = "90%";

  let inputContainerWidth = inputWidth;
  if (isFull) inputContainerWidth = "70%";

  let className = "mb-2 d-flex";
  if (isInteractive()) className += " input-nav-selector";

  return (
    <div ref={props.scoringRef} onClick={onClick} className={className} id={props.uniqueId} style={{ maxWidth: width, minWidth: width }}>
      {!hideLabel && (
        <Button block className="mr-3" style={styles.label}>
          {fromDevice && <FaLaptopMedical className="float-left" />}
          {translateText(name)}
        </Button>
      )}
      <div className="mr-3" style={{ display: "flex", width: inputContainerWidth, minWidth: inputContainerWidth }}>
        {renderInput()}
      </div>
    </div>
  );

  function isInteractive() {
    if (readOnly) return false;
    return true;
  }

  function onClick(e) {
    e.stopPropagation();
    e.preventDefault();
    if (isInteractive()) return onClickInput(e);
  }

  function renderCaret() {
    if (inputValue) {
      const numVal = _toNumber(inputValue.value);
      if (_isNumber(min) && numVal < min) {
        return (
          <span>
            <FaAngleDown size="22" style={styles.caret} />
          </span>
        );
      } else if (_isNumber(max) && numVal > max) {
        return (
          <span>
            <FaAngleUp size="22" style={styles.caret} />
          </span>
        );
      }
    }
    return "";
  }

  async function loadMedicationOptions() {
    const resp = await postDataAPI("/flowsheet/get-medications", { searchChars: medicationRef.current });
    return resp || [];
  }

  //Render input function, it creates the input text-boxes with different css format
  function renderInput() {
    const [isFocused, setIsFocused] = useState(false);
    // Check if the uniqueId is "remarks" to conditionally render a textarea to have multiline
    if (props.type === "multiline") {
      return (
        <textarea
          spellCheck={false}
          innerRef={inputRef}
          type="text"
          value={displayText}
          onChange={(e) => {
            let val = e.target.value;
            if (hasUnit) val = val.replace(` ${unit}`, "");
            onChange(val);
          }}
          style={{
            resize: "vertical",
            height: "250px",
            width: "100%",
            overflowY: "auto", // Enable vertical scrolling
            overflowX: "hidden", // Disable horizontal scrolling
            backgroundColor: isFocused ? "white" : "rgba(55, 55, 55, 0.2)", // Change background on focus
            fontSize: "20px",
            lineHeight: "2",
            letterSpacing: "0.05em",
            border: "0",
            outline: "0",
            textAlign: "left",
            position: "relative", // Position it in a way that it won't be covered by the keyboard
            top: isFocused ? "-10px" : "0", // Adjust position when focused
            color: isFocused ? "black" : "var(--white)", // Change text color on focus
          }}
        />
      );
    } else if (readOnly || (type !== "numeric" && type !== "text")) {
      let className = "txt-container";
      if (readOnly) className += " read-only";
      return (
        <div ref={inputRef} className={className} tabIndex="1">
          {!table && has_sub_groups && hasScore && (
            <div style={{ width: "20%", marginRight: "10px", whiteSpace: "nowrap" }}>
              {translateText("tot")}: {sum}
            </div>
          )}
          <span style={{ whiteSpace: hasUnit && "nowrap" }}>{displayText}</span>
          {type === "numeric" && table && renderCaret()}
        </div>
      );
    } else if (type === "numeric" || type === "text" || type === "multiline") {
      return (
        <div style={styles.inputWrapper}>
          {name === "medication_name" ? (
            <div style={{ width: "100%", height: "100%" }}>
              <AsyncSelect
                cacheOptions
                styles={colourStyles}
                innerRef={inputRef}
                onInputChange={(e) => {
                  medicationRef.current = e;
                }}
                loadOptions={loadMedicationOptions}
                onChange={(e) => {
                  let val = e.label;
                  if (hasUnit) val = val.replace(` ${unit}`, "");
                  onChange(val);
                }}
                placeholder="Search..."
              />
            </div>
          ) : (
            <Input
              spellCheck={false}
              innerRef={inputRef}
              type="text"
              value={displayText}
              onChange={(e) => {
                let val = e.target.value;
                if (hasUnit) val = val.replace(` ${unit}`, "");
                onChange(val);
              }}
            />
          )}
        </div>
      );
    }
  }

  function onClickInput(e) {
    if (readOnly) return;
    if (onClickEffect) {
      if (type === "multiselect") {
        onClickEffect(value);
      } else {
        onClickEffect(inputValue);
      }
    }

    openTouchInput({
      element: inputRef.current ? inputRef.current : e.target,
      inputName: name,
      onChange,
      defaultValue: inputValue,
      type,
      list,
      event: e,
      isFilter,
      units,
      unit,
      config,
      uniqueId: props.uniqueId,
      useTouchInputNav,
      listOptionsWidth,
      allData: allData || data,
    });
  }
}

export default FormField;

const styles = {
  caret: {
    color: "var(--primary)",
    marginBottom: 5,
    marginRight: -20,
  },
  label: {
    width: "inherit",
    flexGrow: 1,
    minHeight: "var(--formfield-height)",
  },
  inputWrapper: {
    // display: "flex",
    height: "100%",
    width: "100%",
  },
  unit: {
    fontSize: "18px",
    backgroundColor: "#ffffffbb",
    marginLeft: 10,
    paddingLeft: 15,
    paddingRight: 15,
    paddingTop: 2,
    borderRadius: ".25em",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
};

const colourStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#fff" : "rgba(55, 55, 55, .2)",
    borderColor: "rgba(0,0,0,0.5)",
    textAlign: "center",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "rgba(55, 55, 55, .8)" : "#fff",
    color: state.isFocused ? "#fff" : "black",
  }),
  placeholder: (provided, state) => ({
    ...provided,
    color: state.isFocused ? "black" : "white",
  }),
  singleValue: (provided, state) => ({
    ...provided,
    color: state.isFocused ? "black" : "white",
  }),
};